'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does Fenty Beauty Have Microplastics? Every Ingredient List Checked"
      author="MicroPlastics Research Desk"
      date="July 31, 2026"
      readTime="11 min read"
      heroImage="/blog/heroes/cosmetics.webp"
      heroImageAlt="Does Fenty Beauty have microplastics PMMA blurring powder PET glitter brow pencil ingredients 2026"
    >
      <BlogLastUpdated iso="2026-07-31" formatted="July 31, 2026" />

      <BlogTLDR>
        <strong>Yes, in several products, and Fenty publishes the
        evidence itself.</strong> Of the 14 distinct Fenty formulas we
        could capture from fentybeauty.com on 30 July 2026,{' '}
        <strong>8 list a solid synthetic polymer particle</strong>. The
        three worth knowing: <strong>Bright Fix Instant Brightening
        &amp; Blurring Powder</strong> lists <strong>Polymethyl
        Methacrylate</strong> (acrylic) as its <strong>fourth</strong>{' '}
        ingredient; <strong>Brown&apos;d Pout</strong> lists{' '}
        <strong>Polyethylene Terephthalate</strong> &mdash; PET, the
        water-bottle plastic, used here as glitter; and the{' '}
        <strong>Brow MVP Ultra Fine Brow Pencil Styler</strong> lists{' '}
        <strong>polyethylene</strong> across all 14 shades. None of it
        breaks any law. Make-up is not restricted in the EU until{' '}
        <strong>17 October 2035</strong>. Separately worth noting:{' '}
        <strong>Fenty does not publish an ingredient list on the product
        page for Pro Filt&apos;r</strong>, its best-known foundation, so
        we make no claim about it.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>PMMA in the blurring powder is the headline.</strong> It is the fourth ingredient, and it is not an additive, the acrylic microspheres <em>are</em> the blur effect.</>,
          <><strong>PET in Brown&apos;d Pout.</strong> Polyethylene terephthalate is the same polymer as a disposable water bottle. Cut into coated flakes it becomes cosmetic glitter.</>,
          <><strong>Polyethylene in the brow pencils</strong> (14 shades) and the Brow MVP sculpting wax, which also lists methyl methacrylate crosspolymer. Standard wax-structure chemistry.</>,
          <>Silicone <strong>elastomer</strong> crosspolymers in Bright Fix Eye Brightener (11 shades) and the Blurred Bronzed duo. These count as solid particles; ordinary liquid dimethicone does not.</>,
          <>The Big Drippr body washes list <strong>acrylates crosspolymer</strong>, and body wash is <strong>rinse-off</strong>, the category the EU restricts first, in October 2027.</>,
          <>Our sample skews toward products where Fenty publishes a parseable list. <strong>8 of 14 is not &ldquo;57% of Fenty&rdquo;</strong> and we are not going to present it that way.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What we checked, and what we could not
      </h2>
      <p>
        We walked fentybeauty.com&apos;s own product sitemap on{' '}
        <strong>30 July 2026</strong> and pulled the ingredient list from
        every product page that publishes one. That produced 59 SKUs,
        which collapse to <strong>14 distinct formulas</strong> once
        shade variants are merged.
      </p>
      <p>
        The gap is the interesting part. Fenty lists several hundred
        product URLs. Most of those pages did not carry a machine-readable
        ingredient list, including the page for{' '}
        <strong>Pro Filt&apos;r Soft Matte Longwear Foundation</strong>,
        the product the brand is best known for.
      </p>
      <p>
        We are not going to tell you what is in Pro Filt&apos;r on the
        strength of a third-party database. Several of the popular ones
        are user-uploaded and years out of date, and a wrong claim about
        a named company&apos;s product is the one mistake worth actually
        worrying about. <strong>If Fenty does not publish it on the
        product page, our answer is &ldquo;not disclosed there,&rdquo;
        and you should check the carton.</strong>
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The findings
      </h2>

      <BlogComparisonTable
        caption="Fenty Beauty products whose published ingredient list includes a solid synthetic polymer (checked 30 July 2026)"
        columns={[
          { key: 'product', label: 'Product' },
          { key: 'polymer', label: 'Solid polymer listed' },
          { key: 'pos', label: 'Role' },
          { key: 'deadline', label: 'EU deadline' },
        ]}
        rows={[
          { product: <>Bright Fix Instant Brightening & Blurring Powder (6 shades)</>, polymer: 'Polymethyl Methacrylate (PMMA)', pos: <>Listed 4th, after mica, synthetic fluorphlogopite and magnesium myristate. Acrylic microspheres scatter light, that is the “blur.”</>, deadline: 'Oct 2035' },
          { product: <>Brown&rsquo;d Pout</>, polymer: 'Polyethylene Terephthalate (PET) + Acrylates Copolymer', pos: 'PET flakes are cosmetic glitter, listed alongside calcium sodium borosilicate and calcium aluminum borosilicate pearls.', deadline: 'Oct 2035' },
          { product: 'Brow MVP Ultra Fine Brow Pencil Styler (14 shades)', polymer: 'Polyethylene', pos: 'Hardens the wax core so a fine tip holds shape.', deadline: 'Oct 2035' },
          { product: 'Brow MVP Sculpting Wax Pencil Styler', polymer: 'Methyl Methacrylate Crosspolymer + Polyethylene', pos: 'Wax structure plus acrylic beads for slip.', deadline: 'Oct 2035' },
          { product: 'Bright Fix Eye Brightener (11 shades)', polymer: 'Dimethicone/Vinyl Dimethicone Crosspolymer', pos: 'Silicone elastomer. Solid swollen gel particles, unlike liquid dimethicone.', deadline: 'Oct 2035' },
          { product: <>Blurred Bronzed Skin Tint & Bronzer Duo</>, polymer: 'Dimethicone/Vinyl Dimethicone Crosspolymer', pos: 'Same silicone elastomer, for soft-focus texture.', deadline: 'Oct 2035' },
          { product: 'Big Drippr Body Wash (4 scents)', polymer: 'Acrylates Crosspolymer', pos: 'Thickener / rheology modifier.', deadline: <><strong>Oct 2027</strong> — rinse-off, restricted first</> },
          { product: 'A$AP Rocky x Fenty Skin lip balm SPF duo', polymer: 'Polyethylene', pos: 'Wax hardener in the balm stick.', deadline: 'Oct 2035' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why PET in a lip product is the one that stings
      </h2>
      <p>
        Most of what is on that table is unremarkable formulation
        chemistry. Polyethylene in a pencil is a wax hardener; it has
        been in pencils for decades. Silicone elastomers give texture.
        These are boring.
      </p>
      <p>
        <strong>Polyethylene terephthalate is different, because it is
        glitter.</strong> PET is the polymer in a single-use drinks
        bottle. To make cosmetic glitter, PET film is metallised,
        coloured and cut into flakes typically 50 to 200&nbsp;&micro;m
        across. That is dead-centre in the size range the EU restriction
        was written to capture, and glitter is the case ECHA cited most
        prominently when it made the rules.
      </p>
      <p>
        It is also the ingredient with the clearest environmental story.
        A lip topper gets eaten, wiped onto napkins, and washed off. PET
        flakes are not biodegradable on any human timescale.
      </p>
      <p>
        The honest counterweight: the <em>quantity</em> is tiny. A lip
        topper is a few grams and the glitter is a small fraction of it.
        Nobody has shown that wearing PET glitter harms the person
        wearing it. The case against it is environmental and
        cumulative, and it is a real case, but it is not a health scare.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What Fenty gets right
      </h2>
      <p>
        Two things, and it would be dishonest to leave them out:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Where Fenty publishes ingredients, it publishes them completely</strong>, including the full may-contain colorant list. That is more than several competitors manage.</li>
        <li><strong>Six of the fourteen formulas we captured were clean</strong> on this test, including several complexion and lip products. This is not a brand that reflexively reaches for polymer beads.</li>
      </ul>
      <p>
        And a criticism that is genuinely Fenty&apos;s to answer: the
        product pages for its highest-volume complexion products do not
        carry an ingredient list at all. If a shopper cannot read the
        formula before buying, the disclosure is not doing its job.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The swaps
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Instead of a PMMA blurring powder:</strong> a mineral setting powder built on silica, rice starch, tapioca starch, kaolin or arrowroot. These blur by the same optical mechanism without acrylic spheres. Check the list, PMMA and nylon-12 are usually in the top five if present.</li>
        <li><strong>Instead of PET glitter:</strong> look for <em>calcium sodium borosilicate</em> or <em>calcium aluminum borosilicate</em> <strong>without</strong> a polyethylene terephthalate line. Borosilicate is glass, and it delivers the same multichrome sparkle. Many brands now use glass-only shimmer.</li>
        <li><strong>Instead of a polyethylene brow pencil:</strong> pencils structured on candelilla or carnauba wax.</li>
        <li><strong>Highest-value single swap:</strong> the body wash. Rinse-off is where the environmental argument is strongest and where the EU acts first.</li>
      </ul>

      <BlogAppCTA
        headline="Scan your Fenty products"
        sub="PMMA, PET, polyethylene, acrylates crosspolymer, silicone elastomers. Scan any makeup product and the app parses the ingredient list, separates solid polymer particles from liquid silicones, and shows a cleaner same-format swap."
        buttonLabel="Scan a makeup product"
      />

      <p>
        This is one of five brand breakdowns from our{' '}
        <Link href="/blog/microplastics-in-makeup-brands-audited" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          audit of 81 formulas across Fenty, Huda Beauty, rhode and Kylie Cosmetics
        </Link>
        . See also{' '}
        <Link href="/blog/does-huda-beauty-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          Huda Beauty
        </Link>
        ,{' '}
        <Link href="/blog/does-rhode-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          rhode
        </Link>
        ,{' '}
        <Link href="/blog/does-kylie-cosmetics-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          Kylie Cosmetics
        </Link>
        , and{' '}
        <Link href="/blog/clean-beauty-microplastics-loophole" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          clean beauty&apos;s plastic loophole
        </Link>
        .
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>The full INCI list for the exact Fenty SKU and shade from the barcode.</>,
          <>PMMA, PET, polyethylene and acrylates flagged as solid polymer particles, separately from liquid silicones such as dimethicone.</>,
          <>Glitter chemistry specifically: whether the sparkle is PET film or borosilicate glass.</>,
          <>Rinse-off versus leave-on versus make-up, which sets both the environmental weight and the EU deadline.</>,
          <>A cleaner same-format, same-budget swap when the product contains a solid polymer.</>,
        ]}
      />

      <BlogFAQ
        items={[
          {
            question: 'Does Fenty Beauty contain microplastics?',
            answer:
              'Some products do. Of the 14 distinct Fenty formulas we could capture from fentybeauty.com on 30 July 2026, 8 listed a solid synthetic polymer particle. The clearest cases are Polymethyl Methacrylate in Bright Fix Instant Brightening & Blurring Powder, Polyethylene Terephthalate (PET) in Brown&apos;d Pout, and polyethylene in the Brow MVP pencils. Six of the formulas we captured contained none.',
          },
          {
            question: 'Does Fenty Pro Filt’r foundation contain microplastics?',
            answer:
              'We do not know, and we are not going to guess. Fenty does not publish an ingredient list on the Pro Filt’r product page on its own website, and we do not treat user-uploaded third-party ingredient databases as reliable enough to make a chemical claim about a named company&apos;s product. Check the carton or the leaflet in the box, and look for nylon-12, polypropylene, PMMA and acrylates copolymer.',
          },
          {
            question: 'Is the glitter in Fenty lip products plastic?',
            answer:
              'In Brown&apos;d Pout, partly yes. Its published ingredient list includes Polyethylene Terephthalate, which is PET, the same polymer used in disposable drinks bottles, cut into coated flakes to make glitter. It also lists calcium sodium borosilicate and calcium aluminum borosilicate, which are glass-based pearl effects and are not plastic. Cosmetic glitter was one of the clearest targets of the EU microplastics restriction.',
          },
          {
            question: 'Is PMMA in makeup safe?',
            answer:
              'There is no published evidence that PMMA in a cosmetic powder harms the person wearing it. The particles used are far too large to cross intact skin, and PMMA is chemically inert, it is the same material as acrylic glass and is used in medical implants. The two open questions are inhalation, since loose powder is aerosolised near the airway and this is genuinely under-researched, and environmental release, which is why the EU restricted it. We would not tell you it is dangerous, and we would not tell you it is nothing.',
          },
          {
            question: 'When does the EU ban affect Fenty Beauty?',
            answer:
              'By category. The Big Drippr body washes are rinse-off, so 17 October 2027. Any leave-on skincare falls under 17 October 2029. Every make-up and lip product named in this article, the blurring powder, Brown&apos;d Pout, the brow pencils, the eye brightener, falls under 17 October 2035. Nothing Fenty sells today is in breach of the restriction.',
          },
          {
            question: 'Which Fenty products are microplastic-free?',
            answer:
              'Six of the fourteen formulas we captured on 30 July 2026 contained no solid synthetic polymer particle. Because Fenty does not publish ingredient lists for a large part of its catalogue on its own product pages, we cannot give you a complete clean list, and a list that pretends to be complete would be worse than no list. Scan the specific product, or read the carton.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Fenty Beauty',
            year: 2026,
            title: 'Published product ingredient lists (Bright Fix Instant Brightening & Blurring Powder, Brown’d Pout, Brow MVP Ultra Fine Brow Pencil Styler, Brow MVP Sculpting Wax, Bright Fix Eye Brightener, Big Drippr Body Wash), retrieved 30 July 2026',
            publisher: 'fentybeauty.com',
            url: 'https://fentybeauty.com/',
          },
          {
            authors: 'European Commission',
            year: 2023,
            title: 'Commission Regulation (EU) 2023/2055 amending Annex XVII to REACH as regards synthetic polymer microparticles',
            publisher: 'Official Journal of the European Union (EUR-Lex, CELEX 32023R2055)',
            url: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A32023R2055',
          },
          {
            authors: 'European Chemicals Agency',
            year: 2023,
            title: 'Microplastics: restriction on intentionally added microplastics (glitter and cosmetic microparticles)',
            publisher: 'ECHA',
            url: 'https://echa.europa.eu/hot-topics/microplastics',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
